import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle, Shield, Users, Zap, KeyRound } from "lucide-react";
import authService from "@/services/auth.service";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: "", color: "" };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { score: 1, label: "Schwach", color: "bg-red-500" };
    if (score <= 2) return { score: 2, label: t('domain:priority.normal', "Mittel"), color: "bg-orange-500" };
    if (score <= 3) return { score: 3, label: "Gut", color: "bg-yellow-500" };
    if (score <= 4) return { score: 4, label: "Stark", color: "bg-emerald-500" };
    return { score: 5, label: "Sehr stark", color: "bg-emerald-600" };
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    if (!token) {
      setError("Ungültiger Link. Bitte fordern Sie einen neuen Link an.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.resetPassword(token, password);
      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError("Der Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // No token in URL
  if (!token) {
    return (
      <div className="min-h-screen bg-white flex">
        {/* Left Side - Dark Sidebar */}
        <div className="hidden lg:flex lg:w-[45%] bg-[#0f172a] relative overflow-hidden flex-col justify-between p-12">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#0F99FD]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="relative z-10">
            <img src="/brand/vyndesk-logo-light.png" alt="VynDesk Logo" className="h-10 w-auto" />
          </div>
          <div className="relative z-10 max-w-lg">
            <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
              {t('auth:login.password', 'Passwort')} <span className="text-[#0F99FD]">zurücksetzen</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              Setzen Sie Ihr Passwort sicher zurück und erhalten Sie wieder Zugang zu Ihrem Konto.
            </p>
          </div>
          <div className="relative z-10 text-sm text-slate-500">
            © {new Date().getFullYear()} Vyndesk. Alle Rechte vorbehalten.
          </div>
        </div>

        {/* Right Side */}
        <div className="flex-1 flex flex-col justify-center items-center px-8 sm:px-12 lg:px-16 bg-white">
          <div className="w-full max-w-[400px] space-y-8">
            <div className="lg:hidden text-center mb-8">
              <img src="/logo-full.png" alt="VynDesk Logo" className="h-8 w-auto mx-auto" />
            </div>
            <div className="text-center space-y-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="h-7 w-7 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">{t('auth:reset.invalidLink', 'Ungültiger Link')}</h2>
              <p className="text-slate-500 text-sm">
                Dieser Link zum Zurücksetzen des Passworts ist ungültig. Bitte fordern Sie einen neuen Link an.
              </p>
            </div>
            <div className="space-y-3">
              <Button
                className="w-full h-11 bg-[#0F99FD] hover:bg-[#0c84e0] text-white font-semibold"
                onClick={() => navigate("/forgot-password")}
              >
                {t('common:x3.requestNewLink', 'Neuen Link anfordern')}
              </Button>
              <Button
                variant="outline"
                className="w-full h-11"
                onClick={() => navigate("/login")}
              >
                {t('common:action.backToLogin', 'Zurück zur Anmeldung')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Dark Sidebar */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#0f172a] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0F99FD]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

        {/* Logo */}
        <div className="relative z-10">
          <img src="/brand/vyndesk-logo-light.png" alt="VynDesk Logo" className="h-10 w-auto" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-lg">
          <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
            {t('auth:reset.newPassword', 'Neues Passwort')} <span className="text-[#0F99FD]">festlegen</span>
          </h1>
          <p className="text-lg text-slate-400 mb-8 leading-relaxed">
            {t('common:x3.chooseSecurePassword', 'Wählen Sie ein sicheres Passwort, um Ihr Konto zu schützen.')}
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:side.s1', 'Zentralisierte Verwaltung')}</p>
                <p className="text-sm text-slate-500">{t('auth:side.s1d', 'Alle Tickets an einem Ort')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Zap className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:side.s2', 'KI-Automatisierung')}</p>
                <p className="text-sm text-slate-500">{t('auth:side.s2d', 'Intelligente Antworten & Routing')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:side.s3', 'Sicher & Zuverlässig')}</p>
                <p className="text-sm text-slate-500">{t('auth:side.s3d', 'Enterprise-Grade Security')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-sm text-slate-500">
          © {new Date().getFullYear()} Vyndesk. Alle Rechte vorbehalten.
        </div>
      </div>

      {/* Right Side - Reset Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-8 sm:px-12 lg:px-16 bg-white">
        <div className="w-full max-w-[400px] space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/logo-full.png" alt="VynDesk Logo" className="h-8 w-auto mx-auto" />
          </div>

          {!success ? (
            <>
              <div className="text-center space-y-2">
                <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <KeyRound className="h-7 w-7 text-slate-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{t('auth:reset.title', 'Neues Passwort festlegen')}</h2>
                <p className="text-slate-500 text-sm">
                  {t('common:x3.enterNewPasswordRestore', 'Geben Sie Ihr neues Passwort ein, um den Zugang wiederherzustellen.')}
                </p>
              </div>

              {error && (
                <Alert variant="destructive" className="border-red-100 bg-red-50 text-red-900">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">{t('auth:reset.newPassword', 'Neues Passwort')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t('auth:reset.min8', 'Mindestens 8 Zeichen')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-11 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                      required
                      disabled={isLoading}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {/* Password Strength Indicator */}
                  {password && (
                    <div className="space-y-1.5">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`h-1.5 flex-1 rounded-full transition-colors ${
                              level <= passwordStrength.score ? passwordStrength.color : "bg-slate-100"
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-slate-500">
                        {t('common:x3.passwordStrength', 'Passwortstärke:')} <span className="font-medium">{passwordStrength.label}</span>
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">{t('auth:reset.confirm', 'Passwort bestätigen')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={t('auth:reset.repeat', 'Passwort wiederholen')}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 pr-10 h-11 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500">{t('auth:reset.mismatch', 'Die Passwörter stimmen nicht überein.')}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0F99FD] hover:bg-[#0c84e0] text-white font-semibold transition-all shadow-sm hover:shadow-md"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('common:state.saving', 'Wird gespeichert...')}
                    </span>
                  ) : (
                    "Passwort speichern"
                  )}
                </Button>
              </form>

              <div className="text-center">
                <p className="text-sm text-slate-500">
                  <Link to="/login" className="font-semibold text-[#0F99FD] hover:text-[#0c84e0] transition-colors">
                    {t('common:action.backToLogin', 'Zurück zur Anmeldung')}
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              <div className="text-center space-y-4">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="h-7 w-7 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{t('auth:reset.changed', 'Passwort geändert!')}</h2>
                <p className="text-slate-500 text-sm">
                  Ihr Passwort wurde erfolgreich geändert. Sie werden in Kürze zur Anmeldung weitergeleitet.
                </p>
              </div>

              <Button
                className="w-full h-11 bg-[#0F99FD] hover:bg-[#0c84e0] text-white font-semibold"
                onClick={() => navigate("/login")}
              >
                {t('common:x3.signInNow', 'Jetzt anmelden')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
